import { createApi } from "@reduxjs/toolkit/query/react";
import { components } from "../../types/api";
import { baseQueryWithReauth } from "../../utils/baseQuery";

// Define types for insertion order operations
type ApiResponseInsertionOrderWithRelations =
  components["schemas"]["ApiResponse_InsertionOrderWithRelations_"];
type ApiResponseInsertionOrderWithRelationsArray =
  components["schemas"]["ApiResponse_InsertionOrderWithRelations-Array_"];

type GenericResponse = components["schemas"]["GenericResponse"];
type BulkDeleteResponse =
  components["schemas"]["ApiResponse_BulkDeleteResult_"];
type ApiResponseCampaignInsertionOrderMetrics =
  components["schemas"]["ApiResponse_CampaignInsertionOrderMetrics_"];
type CampaignInsertionOrderMetrics =
  components["schemas"]["CampaignInsertionOrderMetrics"];

type CampaignInsertionOrderMetricsRequest = {
  campaignId: string;
  startDate?: string;
  endDate?: string;
};

// Request types based on the API operations
type CreateInsertionOrderRequest = {
  campaignId?: string;
  advertiserId?: string;
  insertionOrderId?: string;
  sourceCampaignId?: string;
  displayName: string;
};

type UpdateInsertionOrderRequest = {
  campaignId?: string;
  advertiserId?: string;
  insertionOrderId?: string;
  sourceCampaignId?: string;
  displayName?: string;
};

type BulkDeleteInsertionOrdersRequest = {
  ids: string[];
};

export const insertionOrderApi = createApi({
  reducerPath: "insertionOrderApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["InsertionOrders"],
  endpoints: (builder) => ({
    // Get all insertion orders with optional campaign filter
    getInsertionOrders: builder.query<
      ApiResponseInsertionOrderWithRelationsArray,
      { campaignId?: string } | void
    >({
      query: (params) => ({
        url: "/insertion-orders",
        params: params || {},
      }),
      providesTags: ["InsertionOrders"],
    }),

    // Get insertion order by ID
    getInsertionOrderById: builder.query<
      ApiResponseInsertionOrderWithRelations,
      string
    >({
      query: (id) => `/insertion-orders/${id}`,
      providesTags: (result, error, id) => [
        { type: "InsertionOrders", id },
        "InsertionOrders",
      ],
    }),

    // Get all insertion orders for a specific campaign
    getInsertionOrdersByCampaign: builder.query<
      ApiResponseInsertionOrderWithRelationsArray,
      string
    >({
      query: (campaignId) => `/insertion-orders/campaign/${campaignId}`,
      providesTags: (result, error, campaignId) => [
        { type: "InsertionOrders", id: `campaign-${campaignId}` },
        "InsertionOrders",
      ],
    }),
    // Get all insertion orders for a specific internal campaign
    getInsertionOrdersByInternalCampaign: builder.query<
      ApiResponseInsertionOrderWithRelationsArray,
      string
    >({
      query: (internalCampaignId) =>
        `/insertion-orders/internal-campaign/${internalCampaignId}`,
      providesTags: (result, error, internalCampaignId) => [
        {
          type: "InsertionOrders",
          id: `internal-campaign-${internalCampaignId}`,
        },
        "InsertionOrders",
      ],
    }),

    // Create a new insertion order
    createInsertionOrder: builder.mutation<
      ApiResponseInsertionOrderWithRelations,
      CreateInsertionOrderRequest
    >({
      query: (newInsertionOrder) => ({
        url: "/insertion-orders",
        method: "POST",
        body: newInsertionOrder,
      }),
      invalidatesTags: ["InsertionOrders"],
    }),

    // Update an existing insertion order
    updateInsertionOrder: builder.mutation<
      ApiResponseInsertionOrderWithRelations,
      { id: string; data: UpdateInsertionOrderRequest }
    >({
      query: ({ id, data }) => ({
        url: `/insertion-orders/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "InsertionOrders", id },
        "InsertionOrders",
      ],
    }),

    // Delete an insertion order
    deleteInsertionOrder: builder.mutation<GenericResponse, string>({
      query: (id) => ({
        url: `/insertion-orders/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "InsertionOrders", id },
        "InsertionOrders",
      ],
    }),

    // Get aggregated insertion order metrics for a campaign
    getCampaignInsertionOrderMetrics: builder.query<
      CampaignInsertionOrderMetrics | null,
      CampaignInsertionOrderMetricsRequest
    >({
      query: ({ campaignId, startDate, endDate }) => ({
        url: `/insertion-orders/campaign/${campaignId}/metrics`,
        params: {
          ...(startDate ? { startDate } : {}),
          ...(endDate ? { endDate } : {}),
        },
      }),
      transformResponse: (
        response: ApiResponseCampaignInsertionOrderMetrics
      ): CampaignInsertionOrderMetrics | null => response.data ?? null,
      providesTags: (result, error, { campaignId }) => [
        { type: "InsertionOrders", id: `campaign-metrics-${campaignId}` },
        "InsertionOrders",
      ],
    }),

    // Bulk delete insertion orders
    bulkDeleteInsertionOrders: builder.mutation<
      BulkDeleteResponse,
      BulkDeleteInsertionOrdersRequest
    >({
      query: (data) => ({
        url: "/insertion-orders/bulk-delete",
        method: "DELETE",
        body: data,
      }),
      invalidatesTags: ["InsertionOrders"],
    }),
  }),
});

export const {
  useGetInsertionOrdersQuery,
  useGetInsertionOrderByIdQuery,
  useGetInsertionOrdersByCampaignQuery,
  useGetInsertionOrdersByInternalCampaignQuery,
  useGetCampaignInsertionOrderMetricsQuery,
  useLazyGetCampaignInsertionOrderMetricsQuery,
  useCreateInsertionOrderMutation,
  useUpdateInsertionOrderMutation,
  useDeleteInsertionOrderMutation,
  useBulkDeleteInsertionOrdersMutation,
} = insertionOrderApi;
